import {
  getLayerDocumentTabValue,
  LAYER_VIEW_DEFAULT_TAB,
  LAYER_VIEW_TABS_ID,
} from "@/v2-backoffice-client/layer/layer.constants"
import type { LayerEditorField } from "@/v2-backoffice-client/layer/update/schema/layer.update.schema"
import type { LayerDriftEntry } from "@/v2-backoffice-client/layer/drift/layer.drift.utils"

/**
 * The token whose references the operator is being sent to look at, and —
 * for a rename — what it should become. Two params rather than one encoded
 * pair: field names are operator-supplied and any separator we picked
 * could turn up inside one.
 */
export const DRIFT_TOKEN_PARAM = "driftToken"
export const DRIFT_RENAME_PARAM = "driftRename"

/** Mirrors layerdrift.RemediationKind on the Go side. */
export const REMEDIATION_RENAME_TOKEN = "rename-token"
export const REMEDIATION_REVIEW_REFERENCES = "review-references"

export type LayerDriftRemediation = NonNullable<LayerDriftEntry["Remediation"]>

/**
 * What a document tab needs to know when it opens with a fix pending: the
 * token to find, and the replacement when there is one. Derived from the
 * URL rather than held in memory, so the hand-off survives a reload and a
 * half-finished fix can be handed to a colleague as a link.
 */
export type LayerDriftFixIntent = {
  token: string
  renameTo: string | null
}

export function escapeForRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/**
 * The frontend's half of the backend's whole-token match: "Kostnad"
 * matches `"field": "Kostnad"` but not `"Kostnader"`. Used to decide
 * whether a drift finding still applies to the text in the editor, so a
 * fix stops being reported the moment it is typed rather than after a
 * save and a re-probe.
 */
export function referencesToken(text: string, token: string): boolean {
  if (!text || !token) return false
  return new RegExp(`\\b${escapeForRegex(token)}\\b`).test(text)
}

/**
 * A partial href (see resolvePartialHref) that switches the sheet to
 * `document` with the fix staged, preserving which layer is open.
 *
 * A link, not a click handler: an operator mid-triage can open the fix in
 * a new tab and keep the drift list where it is.
 */
export function getDriftResolveHref(
  remediation: LayerDriftRemediation,
  document: string,
): string {
  const params = new URLSearchParams()
  params.set(`${LAYER_VIEW_TABS_ID}-tab`, getLayerDocumentTabValue(document))
  params.set(DRIFT_TOKEN_PARAM, remediation.FromToken ?? "")
  if (remediation.Kind === REMEDIATION_RENAME_TOKEN && remediation.ToToken) {
    params.set(DRIFT_RENAME_PARAM, remediation.ToToken)
  }
  return `?${params.toString()}`
}

/**
 * Reads a pending fix out of the URL for one document, or null when the
 * URL isn't addressing this document. Every editor tab calls this; only
 * the targeted one gets an intent back.
 */
export function readDriftFixIntent(
  searchParams: URLSearchParams,
  field: LayerEditorField,
): LayerDriftFixIntent | null {
  const token = searchParams.get(DRIFT_TOKEN_PARAM)
  if (!token) return null

  const activeTab =
    searchParams.get(`${LAYER_VIEW_TABS_ID}-tab`) ?? LAYER_VIEW_DEFAULT_TAB
  if (activeTab !== getLayerDocumentTabValue(field)) return null

  return { token, renameTo: searchParams.get(DRIFT_RENAME_PARAM) }
}

/**
 * Drops the fix params once the edit has been staged, so a reload doesn't
 * re-apply a rename the operator has since undone.
 */
export function clearDriftFixParams(searchParams: URLSearchParams) {
  searchParams.delete(DRIFT_TOKEN_PARAM)
  searchParams.delete(DRIFT_RENAME_PARAM)
  return searchParams
}

/**
 * Breaking changes the layer hasn't been adapted to yet — an entry earns
 * a remediation precisely when one of the layer's documents still names
 * what changed upstream.
 *
 * This is what makes accepting a baseline the end of the flow rather than
 * a way around it. The count needs no bookkeeping: fix the references and
 * save, and the next drift read finds no mention of the old token, so the
 * entry falls to a warning and the gate opens on its own.
 */
export function countUnresolvedBreaking(entries: LayerDriftEntry[]): number {
  return entries.filter(
    (entry) => entry.Severity === "breaking" && entry.Remediation != null,
  ).length
}
